const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

function isCalendarDate(value) {
  const match = DATE_PATTERN.exec(value ?? "");
  if (!match) return false;

  const [, year, month, day] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function getCurrentCalendarDate(timeZone, now = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
    return `${values.year}-${values.month}-${values.day}`;
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

function getCurrentCalendarTime(timeZone, now = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
    return `${values.hour}:${values.minute}`;
  } catch {
    return now.toISOString().slice(11, 16);
  }
}

function getEventStartTime(event) {
  return [event?.practice, event?.quali, event?.race]
    .filter((value) => TIME_PATTERN.test(value ?? ""))
    .sort()[0];
}

function getUpcomingEvents(events, timeZone, limit = 6, now = new Date()) {
  const today = getCurrentCalendarDate(timeZone, now);
  const currentTime = getCurrentCalendarTime(timeZone, now);

  return events
    .filter((event) => {
      if (!isCalendarDate(event?.date) || event.date < today) return false;
      if (event.date > today) return true;

      const startTime = getEventStartTime(event);
      return !startTime || startTime >= currentTime;
    })
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, limit);
}

function getNextEventPerSeasonGame(events, timeZone, now = new Date()) {
  const seenSeasonGames = new Set();

  return getUpcomingEvents(events, timeZone, Number.POSITIVE_INFINITY, now).filter((event) => {
    if (!event?.game) return false;

    const seasonGame = JSON.stringify([event.game, event.season ?? null]);
    if (seenSeasonGames.has(seasonGame)) return false;

    seenSeasonGames.add(seasonGame);
    return true;
  });
}

function formatCalendarDate(value, locale, options = {}) {
  if (!isCalendarDate(value)) return value ?? "—";

  const [year, month, day] = value.split("-").map(Number);
  return new Intl.DateTimeFormat(locale, {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "2-digit",
    ...options,
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

export {
  formatCalendarDate,
  getCurrentCalendarDate,
  getCurrentCalendarTime,
  getEventStartTime,
  getNextEventPerSeasonGame,
  getUpcomingEvents,
  isCalendarDate,
};
